import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ZoomIn, ChevronLeft, ChevronRight } from "lucide-react";
import { TITLES } from "@/lib/data";
import { BloodDrip } from "@/components/BloodDrip";

const FILTERS = [
  { key: "all", label: "All Titles" },
  { key: "new", label: "New Issues" },
  { key: "bestseller", label: "Bestsellers" },
  { key: "uncensored", label: "Uncensored" },
];

export default function Titles() {
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const visible = TITLES.filter((t) => filter === "all" || t.tags.includes(filter));
  const activeIndex = visible.findIndex((t) => t.title === active?.title);
  const cycle = (dir) => {
    if (activeIndex < 0 || visible.length < 2) return;
    setActive(visible[(activeIndex + dir + visible.length) % visible.length]);
  };

  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
      if (e.key === "ArrowRight") cycle(1);
      if (e.key === "ArrowLeft") cycle(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  return (
    <section id="titles" className="relative py-28 sm:py-36 bg-pulp/40">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="font-marker text-paper text-base sm:text-lg mb-3 rotate-1 inline-block"
        >
          she's waiting between these pages
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase text-paper mb-8 sm:mb-10"
        >
          THE <span className="text-crimson">TITLES</span>
          <BloodDrip />
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="flex flex-wrap gap-3 mb-10 sm:mb-12"
          data-testid="titles-filter-bar"
        >
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`titles-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`font-marker text-xs sm:text-sm px-4 py-2 border-2 transition-colors duration-150 ${
                filter === f.key
                  ? "bg-crimson border-crimson text-white ink-shadow-sm"
                  : "border-crimson/35 text-paper/60 hover:text-paper hover:border-crimson"
              }`}
            >
              {f.label}
            </button>
          ))}
        </motion.div>

        <motion.div
          layout
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10"
        >
          <AnimatePresence mode="popLayout">
            {visible.map((t, i) => (
              <motion.button
                layout
                key={t.title}
                data-testid={`title-card-${i}`}
                onClick={() => setActive(t)}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.25 }}
                className="group text-left"
              >
                <div className="spotlight-frame aspect-[2/3] mb-5">
                  <img
                    src={t.cover}
                    alt={`${t.title} comic cover`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 halftone opacity-0 group-hover:opacity-20 transition-opacity duration-300 pointer-events-none" />
                  <div className="absolute inset-0 bg-void/0 group-hover:bg-void/40 transition-colors duration-300 flex items-center justify-center pointer-events-none">
                    <ZoomIn className="text-yell opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={30} />
                  </div>
                  <span
                    className="absolute top-3 -left-2 -rotate-3 bg-blooddeep text-white font-marker text-xs px-3 py-1 ink-shadow-sm"
                  >
                    {t.badge}
                  </span>
                </div>
                <h3 className="font-impact uppercase tracking-wide text-xl sm:text-2xl text-paper transition-colors duration-150 group-hover:text-crimson">
                  {t.title}
                </h3>
                <p className="font-type text-xs sm:text-sm text-paper/55 mt-1 min-h-8 sm:min-h-10 line-clamp-2">{t.tagline}</p>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="title-lightbox-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-50 bg-void/92 backdrop-blur-md flex items-center justify-center p-4 sm:p-10"
          >
            {visible.length > 1 && (
              <>
                <button
                  data-testid="title-lightbox-prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    cycle(-1);
                  }}
                  className="absolute left-3 sm:left-8 top-1/2 -translate-y-1/2 bg-crimson text-white border-2 border-black p-2 ink-shadow-sm hover:bg-crimsonhot transition-colors duration-150"
                  aria-label="Previous cover"
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  data-testid="title-lightbox-next"
                  onClick={(e) => {
                    e.stopPropagation();
                    cycle(1);
                  }}
                  className="absolute right-3 sm:right-8 top-1/2 -translate-y-1/2 bg-crimson text-white border-2 border-black p-2 ink-shadow-sm hover:bg-crimsonhot transition-colors duration-150"
                  aria-label="Next cover"
                >
                  <ChevronRight size={22} />
                </button>
              </>
            )}
            <motion.figure
              initial={{ scale: 0.9, rotate: -1 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-full flex flex-col items-center"
            >
              <img
                src={active.cover}
                alt={`${active.title} comic cover`}
                className="max-h-[62vh] w-auto border-4 border-crimson comic-shadow"
              />
              <figcaption className="absolute top-4 -left-4 bg-blooddeep text-white font-marker text-sm px-4 py-1.5 -rotate-2 ink-shadow-sm">
                {active.title} — {active.tagline}
              </figcaption>
              <button
                data-testid="title-lightbox-close"
                onClick={() => setActive(null)}
                className="absolute -top-4 -right-4 bg-crimson text-white border-2 border-black p-2 ink-shadow-sm hover:bg-crimsonhot transition-colors duration-150"
                aria-label="Close lightbox"
              >
                <X size={20} />
              </button>
              <button
                data-testid="title-lightbox-shop-button"
                onClick={() => (window.location.href = "/shop")}
                className="mt-8 bg-crimson text-white font-marker text-sm px-8 py-3 border-2 border-black ink-shadow-sm hover:bg-crimsonhot transition-colors duration-150"
              >
                SHOP THIS TITLE
              </button>
              <span data-testid="title-lightbox-counter" className="mt-3 font-courier text-xs text-paper/50">
                {activeIndex + 1} / {visible.length}
              </span>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
